import type { GridApi } from '@mui/x-data-grid-pro';
import { gridExpandedSortedRowIdsSelector, gridSortModelSelector } from '@mui/x-data-grid-pro';
import { useQueries, useQueryClient } from '@tanstack/react-query';
import * as React from 'react';
import type { DatasetPage } from './useDatasetPages';

export type PageData<V> = {
  valuesById: ReadonlyMap<number, V>;
  missingIds: ReadonlySet<number>;
};

const EMPTY_PAGE_DATA: PageData<unknown> = {
  valuesById: new Map(),
  missingIds: new Set(),
};

const emptyPageData = <V>(): PageData<V> => EMPTY_PAGE_DATA as PageData<V>;

export const pageQueryKey = (
  providerName: string,
  instanceKey: string,
  datasetSignature: string,
  pageIndex: number,
) => [providerName, instanceKey, 'page', datasetSignature, pageIndex] as const;

type FetchByIds<V> = (
  ids: readonly number[],
  signal: AbortSignal,
) => Promise<ReadonlyMap<number, V>>;

const mergePageData = <V>(
  previous: PageData<V>,
  ids: readonly number[],
  fetched: ReadonlyMap<number, V>,
): PageData<V> => {
  const valuesById = new Map(previous.valuesById);
  const missingIds = new Set(previous.missingIds);
  for (const [id, value] of fetched) valuesById.set(id, value);
  for (const id of ids) {
    if (!fetched.has(id)) missingIds.add(id);
  }
  return { valuesById, missingIds };
};

export const topLevelIdSet = (pages: readonly DatasetPage[]): Set<number> => {
  const set = new Set<number>();
  for (const page of pages) for (const id of page.topLevelIds) set.add(id);
  return set;
};

export const sameSet = (a: ReadonlySet<number>, b: ReadonlySet<number>): boolean => {
  if (a.size !== b.size) return false;
  for (const id of a) if (!b.has(id)) return false;
  return true;
};

// Carry loaded values (top-level and fetched descendants) from the old-signature
// pages into the new page keys, re-partitioned by pageIndexById, to avoid a refetch.
export const carryForwardPageCache = <V>(
  queryClient: ReturnType<typeof useQueryClient>,
  providerName: string,
  instanceKey: string,
  oldSignature: string,
  oldPages: readonly DatasetPage[],
  newSignature: string,
  newPageIndexById: ReadonlyMap<number, number>,
): void => {
  const nextPages = new Map<number, PageData<V>>();
  const bucket = (pageIndex: number): PageData<V> => {
    let data = nextPages.get(pageIndex);
    if (!data) {
      data = { valuesById: new Map<number, V>(), missingIds: new Set<number>() };
      nextPages.set(pageIndex, data);
    }
    return data;
  };

  let migrated = false;
  for (let i = 0; i < oldPages.length; i++) {
    const data = queryClient.getQueryData<PageData<V>>(
      pageQueryKey(providerName, instanceKey, oldSignature, i),
    );
    if (!data) continue;
    for (const [id, value] of data.valuesById) {
      const pageIndex = newPageIndexById.get(id);
      if (pageIndex === undefined) continue;
      (bucket(pageIndex).valuesById as Map<number, V>).set(id, value);
      migrated = true;
    }
    for (const id of data.missingIds) {
      const pageIndex = newPageIndexById.get(id);
      if (pageIndex === undefined) continue;
      (bucket(pageIndex).missingIds as Set<number>).add(id);
      migrated = true;
    }
  }
  if (!migrated) return;

  for (const [pageIndex, data] of nextPages) {
    const key = pageQueryKey(providerName, instanceKey, newSignature, pageIndex);
    queryClient.setQueryData<PageData<V>>(key, data);
    // Seeding marks the query fresh (staleTime: Infinity), so invalidate to let
    // queryFn fetch ids the new composition added, keeping the migrated rows.
    void queryClient.invalidateQueries({ queryKey: key, exact: true });
  }
};

export type UseTablePagedQueryResult<V> = {
  loadedById: ReadonlyMap<number, V>;
  missingIds: ReadonlySet<number>;
  isPending: boolean;
};

/**
 * Paged value cache for items-table providers. One React Query entry per page;
 * observed pages stay subscribed for the dataset's lifetime so scroll-back
 * doesn't refetch. Expanding a parent fetches its descendants into the same
 * page entry via setQueryData — the page query itself never refetches.
 */
export const useTablePagedQuery = <V>({
  providerName,
  instanceKey,
  pages,
  visiblePageIndices,
  pageIndexById,
  datasetSignature,
  fetchByIds,
  enabled = true,
  apiRef,
}: {
  providerName: string;
  instanceKey: string;
  pages: readonly DatasetPage[];
  visiblePageIndices: readonly number[];
  pageIndexById: ReadonlyMap<number, number>;
  datasetSignature: string;
  fetchByIds: FetchByIds<V>;
  enabled?: boolean;
  apiRef: React.RefObject<GridApi>;
}): UseTablePagedQueryResult<V> => {
  const queryClient = useQueryClient();

  const fetchByIdsRef = React.useRef(fetchByIds);
  fetchByIdsRef.current = fetchByIds;

  // Pages stay observed for the dataset's lifetime; reset on signature flip.
  const subscribedRef = React.useRef<Set<number>>(new Set());
  const lastSignatureRef = React.useRef<string>(datasetSignature);
  const lastPagesRef = React.useRef<readonly DatasetPage[]>(pages);
  const pendingMigrationRef = React.useRef<{
    fromSignature: string;
    fromPages: readonly DatasetPage[];
  } | null>(null);

  // Aborts in-flight descendant-prefetch fetches. Scoped to the datasetSignature
  //   (not to every re-render) so ordinary page landings don't cancel legitimate
  //   fetches, but a signature flip — e.g. a reschedule reordering the rows — does:
  //   an in-flight fetch started before the flip carries now-stale values that must
  //   not be written back into the freshly-evicted page cache.
  const descendantControllerRef = React.useRef<AbortController>(new AbortController());

  if (lastSignatureRef.current !== datasetSignature) {
    // Any in-flight descendant fetch predates this flip and is now stale.
    descendantControllerRef.current.abort();
    descendantControllerRef.current = new AbortController();
    // A same-set flip with no active sort is a reorder: keep subscriptions and
    // migrate the page cache forward (post-commit) to avoid the row-flicker. A
    // sort change (or real set change) resets subscriptions and lets pages refetch.
    let isSorted = false;
    try {
      isSorted = apiRef.current ? gridSortModelSelector(apiRef).length > 0 : false;
    } catch {
      isSorted = false;
    }
    if (!isSorted && sameSet(topLevelIdSet(lastPagesRef.current), topLevelIdSet(pages))) {
      pendingMigrationRef.current = {
        fromSignature: lastSignatureRef.current,
        fromPages: lastPagesRef.current,
      };
    } else {
      subscribedRef.current = new Set();
    }
    lastSignatureRef.current = datasetSignature;
  }
  lastPagesRef.current = pages;

  React.useEffect(() => {
    const pending = pendingMigrationRef.current;
    if (!pending) return;
    pendingMigrationRef.current = null;
    carryForwardPageCache<V>(
      queryClient,
      providerName,
      instanceKey,
      pending.fromSignature,
      pending.fromPages,
      datasetSignature,
      pageIndexById,
    );
  }, [queryClient, providerName, instanceKey, datasetSignature, pageIndexById]);

  const subscribedPageIndices = React.useMemo(() => {
    for (const i of visiblePageIndices) subscribedRef.current.add(i);
    return Array.from(subscribedRef.current).sort((a, b) => a - b);
  }, [visiblePageIndices]);

  const queries = useQueries({
    queries: subscribedPageIndices.map((pageIndex) => {
      const page = pages[pageIndex];
      const topLevelIds = page?.topLevelIds ?? [];
      return {
        queryKey: pageQueryKey(providerName, instanceKey, datasetSignature, pageIndex),
        queryFn: async ({ signal }: { signal: AbortSignal }): Promise<PageData<V>> => {
          const previous =
            queryClient.getQueryData<PageData<V>>(
              pageQueryKey(providerName, instanceKey, datasetSignature, pageIndex),
            ) ?? emptyPageData<V>();
          const idsNeeded: number[] = [];
          for (const id of topLevelIds) {
            if (previous.valuesById.has(id) || previous.missingIds.has(id)) continue;
            idsNeeded.push(id);
          }
          if (idsNeeded.length === 0) return previous;
          const fetched = await fetchByIdsRef.current(idsNeeded, signal);
          return mergePageData(previous, idsNeeded, fetched);
        },
        enabled: enabled && topLevelIds.length > 0,
        staleTime: Number.POSITIVE_INFINITY,
      };
    }),
  });

  // Scalar dep that bumps when any page lands; lets effects react to
  // queries[].data without a variable-length dep array.
  const dataVersion = queries.reduce((sum, q) => sum + (q.dataUpdatedAt ?? 0), 0);

  const topLevelIdsByPageRef = React.useRef<ReadonlyMap<number, ReadonlySet<number>>>(new Map());
  topLevelIdsByPageRef.current = React.useMemo(() => {
    const map = new Map<number, Set<number>>();
    for (const page of pages) map.set(page.pageIndex, new Set(page.topLevelIds));
    return map;
  }, [pages]);

  React.useEffect(() => {
    return () => {
      descendantControllerRef.current.abort();
    };
  }, []);

  // De-dupe concurrent triggers so we don't double-issue the same descendants.
  const inFlightDescendantIdsRef = React.useRef<Set<number>>(new Set());

  // biome-ignore lint/correctness/useExhaustiveDependencies: we want the data version to trigger update
  React.useEffect(() => {
    if (!enabled || !apiRef.current) return;

    const run = async () => {
      const state = apiRef.current?.state;
      if (!state) return;
      let expandedIds: readonly (string | number)[];
      try {
        // biome-ignore lint/suspicious/noExplicitAny: MUI selector cannot accept GridStatePro typing
        expandedIds = gridExpandedSortedRowIdsSelector(state as any);
      } catch {
        // Grid state not yet initialised; the next event will retry.
        return;
      }
      if (!expandedIds.length) return;

      const idsByPage = new Map<number, number[]>();
      for (const rowId of expandedIds) {
        const id = Number(rowId);
        if (!Number.isFinite(id)) continue;
        if (inFlightDescendantIdsRef.current.has(id)) continue;
        const pageIndex = pageIndexById.get(id);
        if (pageIndex === undefined) continue;
        if (topLevelIdsByPageRef.current.get(pageIndex)?.has(id)) continue;

        const key = pageQueryKey(providerName, instanceKey, datasetSignature, pageIndex);
        const pageData = queryClient.getQueryData<PageData<V>>(key);
        if (!pageData) continue;
        if (pageData.valuesById.has(id) || pageData.missingIds.has(id)) continue;

        const bucket = idsByPage.get(pageIndex) ?? [];
        bucket.push(id);
        idsByPage.set(pageIndex, bucket);
      }

      if (idsByPage.size === 0) return;

      for (const ids of idsByPage.values()) {
        for (const id of ids) inFlightDescendantIdsRef.current.add(id);
      }

      const signal = descendantControllerRef.current.signal;
      await Promise.allSettled(
        Array.from(idsByPage.entries()).map(async ([pageIndex, ids]) => {
          try {
            const fetched = await fetchByIdsRef.current(ids, signal);
            // Skip ids the cache already has — preserves patchItem writes
            // that may have landed while this fetch was in flight.
            const key = pageQueryKey(providerName, instanceKey, datasetSignature, pageIndex);
            const previous = queryClient.getQueryData<PageData<V>>(key) ?? emptyPageData<V>();
            const idsToWrite = ids.filter((id) => !previous.valuesById.has(id));
            if (idsToWrite.length === 0) return;
            queryClient.setQueryData<PageData<V>>(
              key,
              mergePageData(previous, idsToWrite, fetched),
            );
          } finally {
            for (const id of ids) inFlightDescendantIdsRef.current.delete(id);
          }
        }),
      );
    };

    void run();

    const unsubExpansion = apiRef.current.subscribeEvent('rowExpansionChange', () => {
      void run();
    });
    return () => {
      unsubExpansion();
    };
  }, [
    apiRef,
    enabled,
    instanceKey,
    pageIndexById,
    providerName,
    queryClient,
    dataVersion,
    datasetSignature,
  ]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: dataVersion stands in for queries[].data; spreading would break rules of hooks once new pages subscribe.
  const { loadedById, missingIds } = React.useMemo(() => {
    const merged = new Map<number, V>();
    const missing = new Set<number>();
    for (const q of queries) {
      const data = q.data;
      if (!data) continue;
      for (const [id, value] of data.valuesById) merged.set(id, value);
      for (const id of data.missingIds) missing.add(id);
    }
    return { loadedById: merged, missingIds: missing };
  }, [dataVersion]);

  const isPending = queries.some((q) => q.isPending && q.fetchStatus !== 'idle');

  return { loadedById, missingIds, isPending };
};
